import { Star, ShieldCheck, MapPin, Calendar, Clock3, MessageSquare } from "lucide-react";

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-purple-500",
  "bg-teal-600",
  "bg-orange-500",
  "bg-pink-500",
  "bg-emerald-600",
];

function initials(name) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function money(n) {
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function VendorQuoteCard({ quote, badge, avatarIndex = 0, onCompareToggle, isComparing }) {
  const avatarColor = AVATAR_COLORS[avatarIndex % AVATAR_COLORS.length];

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full ${avatarColor} text-white font-bold text-sm flex items-center justify-center shrink-0`}
          >
            {initials(quote.vendor)}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm leading-tight">{quote.vendor}</p>
            <p className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
              <MapPin className="w-3 h-3" />
              {quote.location}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] font-semibold text-gray-400 tracking-wide">TOTAL</p>
          <p className="text-lg font-extrabold text-indigo-600 leading-tight">{money(quote.total)}</p>
        </div>
      </div>

      {/* Verified + rating */}
      <div className="flex items-center gap-2 mb-2">
        {quote.verified && (
          <span className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
            <ShieldCheck className="w-3 h-3" />
            Verified
          </span>
        )}
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          {quote.rating} · {quote.yearsExp} yrs
        </span>
      </div>

      {/* Optional highlight badge */}
      {badge && (
        <span className="inline-block w-fit text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 mb-3">
          {badge}
        </span>
      )}

      {/* Price breakdown */}
      <div className="text-sm text-gray-600 space-y-1.5 pt-1 border-t border-gray-100 pt-3">
        <div className="flex justify-between">
          <span>Material</span>
          <span>{money(quote.material)}</span>
        </div>
        <div className="flex justify-between">
          <span>Manufacturing</span>
          <span>{money(quote.manufacturing)}</span>
        </div>
        <div className="flex justify-between">
          <span>Tooling</span>
          <span>{money(quote.tooling)}</span>
        </div>
        <div className="flex justify-between">
          <span>Other</span>
          <span>{money(quote.other)}</span>
        </div>
        <div className="flex justify-between">
          <span>GST (18%)</span>
          <span>{money(quote.gst)}</span>
        </div>
      </div>

      <div className="flex justify-between items-center font-semibold text-gray-900 text-sm border-t border-gray-100 mt-3 pt-3">
        <span>Final Quoted Price</span>
        <span>{money(quote.total)}</span>
      </div>

      {/* Delivery / Validity boxes */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-gray-200 rounded-lg px-3 py-2">
          <p className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 tracking-wide mb-0.5">
            <Calendar className="w-3 h-3" />
            DELIVERY
          </p>
          <p className="text-sm font-bold text-gray-900">{quote.delivery} days</p>
        </div>
        <div className="border border-gray-200 rounded-lg px-3 py-2">
          <p className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 tracking-wide mb-0.5">
            <Clock3 className="w-3 h-3" />
            VALIDITY
          </p>
          <p className="text-sm font-bold text-gray-900">{quote.validity} days</p>
        </div>
      </div>

      {/* Payment / warranty */}
      <p className="text-xs text-gray-500 mt-3">
        <span className="font-semibold text-gray-700">Payment:</span> {quote.payment}
      </p>
      <p className="text-xs text-gray-500 mt-1 mb-4">
        <span className="font-semibold text-gray-700">Warranty:</span> {quote.warranty}
      </p>

      {/* Actions */}
      <div className="flex gap-2 mt-auto">
        <button
          type="button"
          onClick={onCompareToggle}
          className={`flex-1 text-sm font-medium px-3 py-2 rounded-xl border transition ${
            isComparing
              ? "bg-indigo-600 border-indigo-600 text-white"
              : "bg-white border-gray-200 text-gray-700 hover:border-indigo-300"
          }`}
        >
          Compare
        </button>
        <button
          type="button"
          className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 text-gray-700 hover:border-indigo-300"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          Message
        </button>
        <button
          type="button"
          className="flex-1 text-sm font-medium px-3 py-2 rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90"
        >
          Accept
        </button>
      </div>
    </div>
  );
}